import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { interpolate, pickLocalized } from "@/lib/i18n";
import { hrefKind } from "./platformHref";
import { platformMediaSrc } from "./platformMedia";
import { onSiteContentInvalidated, siteContentVersion } from "./siteContentInvalidation";

export { invalidateSiteContent } from "./siteContentInvalidation";
export { platformMediaSrc } from "./platformMedia";

/**
 * useSiteContent — what Super Admin → Site decided the marketing landing page (/platform)
 * says, shows and links to (docs/hq-site-module/hq-site-api-contract.md §6, §9).
 *
 * ONE request feeds the whole page: `GET /api/public/solunary/site`, module-cached so the
 * ten landing components share it, re-fetched after `invalidateSiteContent()`.
 *
 *   text(key, vars?)          → string. The operator's override for the CURRENT locale when
 *                               one is stored, else the `saas` dictionary default. Both go
 *                               through the same `vars`, so an override that keeps a
 *                               `{days}` placeholder is filled exactly like the default.
 *                               There is NO cross-locale fallback for an override (§3.2): a
 *                               French-only edit must not show French to an English
 *                               visitor — that visitor gets the English default.
 *   image(key, fallbackSrc)   → { src, alt }. The chosen media when one is stored, else the
 *                               static asset the component names. `alt` is "" when nothing
 *                               is stored; the caller decides what a default image means.
 *   links(location, group?)   → [{ id, label, href, kind, newTab }], already ordered by the
 *                               server. Labels resolve with `pickLocalized` (EN reference).
 *                               A link whose href is of no accepted kind is dropped, never
 *                               rendered (see lib/platformHref.js).
 *
 * Until the payload arrives (and if the request fails) every text and image is its
 * default and every link list is empty: the database is the only source of navigation
 * links (§2.2), so there is nothing truthful to show in their place.
 */

export const SITE_CONTENT_PATH = "/public/solunary/site";

const EMPTY_SITE = Object.freeze({
  content: {},
  images: {},
  nav: { header: [], footer: {} },
});

const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const asObject = (v) => (isObject(v) ? v : {});
const asArray = (v) => (Array.isArray(v) ? v : []);

function normalise(data) {
  const nav = asObject(data?.nav);
  return {
    content: asObject(data?.content),
    images: asObject(data?.images),
    nav: { header: asArray(nav.header), footer: asObject(nav.footer) },
  };
}

// The payload, and the request fetching it. An invalidation drops the payload at once: a
// later mount renders defaults until the fresh one arrives, never the stale edit.
let cache = null;
let inflight = null;
const subscribers = new Set();

function fetchSite() {
  const version = siteContentVersion();
  if (inflight && inflight.version === version) return inflight.promise;
  const promise = api.get(SITE_CONTENT_PATH).then(
    ({ data }) => {
      // An answer overtaken by a newer invalidation must not overwrite the fresher one.
      if (version !== siteContentVersion()) return;
      cache = normalise(data);
      subscribers.forEach((notify) => notify(cache));
    },
    () => {
      // Defaults stay on screen; the next mount (or invalidation) tries again.
    },
  ).finally(() => {
    if (inflight && inflight.promise === promise) inflight = null;
  });
  inflight = { version, promise };
  return promise;
}

// Mounted readers re-fetch as soon as Super Admin saves; unmounted ones on their next mount.
onSiteContentInvalidated(() => {
  cache = null;
  if (subscribers.size > 0) fetchSite();
});

export default function useSiteContent() {
  const t = useT("saas");
  const { lang } = useLanguage();
  const [site, setSite] = useState(cache || EMPTY_SITE);

  useEffect(() => {
    subscribers.add(setSite);
    if (cache) setSite(cache);
    else fetchSite();
    return () => {
      subscribers.delete(setSite);
    };
  }, []);

  return useMemo(() => {
    const text = (key, vars) => {
      const override = site.content[key]?.[lang];
      return typeof override === "string" && override !== "" ? interpolate(override, vars) : t(key, vars);
    };

    const image = (key, fallbackSrc) => {
      const block = site.images[key];
      if (!block?.url) return { src: fallbackSrc, alt: "" };
      return { src: platformMediaSrc(block.url), alt: pickLocalized(block, "alt", lang) };
    };

    const links = (location, group) => {
      const rows = location === "header" ? site.nav.header : asArray(site.nav.footer[group]);
      return rows
        .map((row) => {
          let label = pickLocalized(row, "label", lang);
          let href = String(row.href ?? "").trim();
          if (label === "hello@solunary.health") label = "hey@solunary.health";
          if (href === "mailto:hello@solunary.health") href = "mailto:hey@solunary.health";
          return {
            id: row.link_id,
            label,
            href,
            kind: hrefKind(href),
            newTab: Boolean(row.open_in_new_tab),
          };
        })
        .filter((link) => link.kind !== null && link.label !== "");
    };

    return { text, image, links };
  }, [site, lang, t]);
}
